'use client';

import { createContext, useCallback, useState } from 'react';
import Header from 'components/gruenland/organisms/header/Header';
import { HeaderProps } from 'components/gruenland/organisms/header/types';
import { Market } from 'components/gruenland/organisms/market-button/MarketButton';
import { useDebounce } from 'helpers/hooks/useDebounce';
import { LineItem as CartLineItem } from 'types/entity/cart';
import { LineItem as WishlistLineItem } from 'types/entity/wishlist';
import { useCart, useProduct, useWishlist } from 'frontastic/hooks';
import { TasticProps } from 'frontastic/tastics/types';

const initialMarketState = {
  market: {} as Market,
  markets: [] as Market[],
  handleMarket: {} as (market: Market) => void,
};
export const MarketContext = createContext(initialMarketState);

const HeaderTastic = ({ data, categories }: TasticProps<HeaderProps>) => {
  const [query, setQuery] = useState('');

  const debounecdQuery = useDebounce(query, 150);

  const { products } = useProduct({ query: debounecdQuery, limit: 6 });

  const {
    data: cart,
    isEmpty,
    totalItems: totalCartItems,
    addItem,
    removeItem,
    updateItem,
    redeemDiscountCode,
    removeDiscountCode,
  } = useCart();

  const {
    data: wishlist,
    addToWishlist,
    totalItems: totalWishlistItems,
    removeLineItem,
    deleteWishlist,
  } = useWishlist();

  const moveToWishlist = useCallback(
    async (lineItem: CartLineItem) => {
      if (!wishlist) return;

      await Promise.all([
        await removeItem(lineItem),
        addToWishlist(
          wishlist,
          {
            lineItemId: lineItem.lineItemId ?? '',
            productId: lineItem.productId,
            name: lineItem.name,
            type: lineItem.type,
            count: 1,
            variant: lineItem.variant,
            addedAt: new Date(),
            _url: lineItem._url,
          },
          1,
        ),
      ]);
    },
    [removeItem, addToWishlist, wishlist],
  );

  return (
    <div id="header-container">
      <Header
        cart={cart}
        isEmpty={isEmpty}
        onApplyDiscountCode={redeemDiscountCode}
        onRemoveDiscountCode={removeDiscountCode}
        onRemoveItem={removeItem}
        onUpdateItem={updateItem}
        wishlist={wishlist}
        onRemoveFromWishlist={async (lineItemId) => {
          if (wishlist) {
            await removeLineItem(
              wishlist,
              wishlist.lineItems?.find((item) => item.lineItemId === lineItemId) as WishlistLineItem,
            );
          }
        }}
        onMoveToCart={async (lineItem: WishlistLineItem) => {
          if (wishlist && lineItem.variant) {
            await removeLineItem(
              wishlist,
              wishlist.lineItems?.find((item) => item.lineItemId === lineItem.lineItemId) as WishlistLineItem,
            );

            await addItem(lineItem, lineItem.variant, 1);
          }
        }}
        onClearWishlist={async () => {
          if (wishlist) deleteWishlist(wishlist);
        }}
        totalCartItems={totalCartItems}
        totalWishlistItems={totalWishlistItems}
        navLinks={categories?.filter((category) => category.depth === 0)}
        categories={categories}
        tiles={data.tiles}
        searchItems={products}
        onSearchQueryUpdate={(q) => setQuery(q)}
        col1Label={data.col1Label}
        col1Links={data.col1Links}
        juniorLogoCategories={data.juniorLogoCategories}
      />
    </div>
  );
};
export default HeaderTastic;
